import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import { addPadAttachment, createPadEntry, removePadAttachment, updatePadEntry, uploadPadAttachmentFile } from "./api";
import PadAttachments from "./PadAttachments";
import {
  PAD_CATEGORIES,
  ALTERNATE_METHODS,
  STOP_REASONS,
  categoryMeta,
  totalRentFromItem,
  rentBreakdownFromItem,
  moveOutDateFromItem,
  money,
  monthOf,
} from "./padInfoTypes";

// Visual tokens — mirror the addendum modal so the two sections read as one product.
const C = {
  primary: "#0C447C",
  primaryMd: "#1A6FB5",
  border: "#E4E9F2",
  inputBorder: "#D6DEEA",
  muted: "#64748B",
  text: "#0F1F35",
  soft: "#F7F9FC",
};
const T = { title: 17, section: 15, body: 13.5, label: 12.5, hint: 11.5 };

const styles = {
  body: { padding: "18px 20px 14px", maxHeight: "72vh", overflowY: "auto", background: C.soft },
  label: { fontSize: T.label, fontWeight: 600, color: C.text, marginBottom: 6, display: "block" },
  fieldBox: {
    display: "flex", alignItems: "center", gap: 9, height: 44,
    border: `1px solid ${C.inputBorder}`, borderRadius: 10, padding: "0 12px", background: "#fff",
  },
  bareInput: {
    border: "none", outline: "none", background: "transparent", flex: 1, width: "100%",
    fontSize: T.body, color: C.text, height: "100%", fontFamily: "inherit", minWidth: 0,
  },
  card: { border: `1px solid ${C.border}`, borderRadius: 14, background: "#fff", boxShadow: "0 1px 2px rgba(15,31,53,0.04)", overflow: "hidden" },
  cardHead: { display: "flex", alignItems: "flex-start", gap: 12, padding: "16px 18px 0" },
  cardBody: { padding: "14px 18px 18px" },
  hint: { fontSize: T.hint, fontWeight: 500, color: C.muted, lineHeight: 1.45 },
  sectionTitle: { fontSize: T.section, fontWeight: 700, color: C.text, lineHeight: 1.25 },
  sectionSub: { fontSize: T.hint, fontWeight: 500, color: C.muted, lineHeight: 1.4, marginTop: 3 },
};

/**
 * One numbered step. The badge and the sentence under the heading do the explaining, so the
 * fields themselves can stay unlabelled-by-paragraph and the form reads top to bottom.
 */
const Section = ({ n, title, subtitle, action, children, last }) => (
  <div style={{ ...styles.card, marginBottom: last ? 4 : 14 }}>
    <div style={styles.cardHead}>
      <span className="pim-step">{n}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={styles.sectionTitle}>{title}</div>
        {subtitle ? <div style={styles.sectionSub}>{subtitle}</div> : null}
      </div>
      {action ? <div style={{ flex: "none" }}>{action}</div> : null}
    </div>
    <div style={styles.cardBody}>{children}</div>
  </div>
);

const Field = ({ label, children, hint, style }) => (
  <div style={style}>
    <label style={styles.label}>{label}</label>
    {children}
    {hint ? <div style={{ ...styles.hint, marginTop: 4 }}>{hint}</div> : null}
  </div>
);

const Box = ({ children, className = "", style }) => (
  <div className={`pim-focusable ${className}`} style={{ ...styles.fieldBox, ...style }}>
    {children}
  </div>
);

const emptyForm = (tenantNames = "") => ({
  tenant_names: tenantNames,
  category: "",
  effective_date: "",
  delay: { month: "", resume_date: "" },
  temp_stop: { alternate_method: "", resume_date: "" },
  stop: { reason: "", move_out_date: "" },
  notes: "",
});

const fromExisting = (e) => ({
  tenant_names: e.tenant_names || "",
  category: e.category || "",
  effective_date: e.effective_date || "",
  delay: { month: "", resume_date: "", ...(e.delay || {}) },
  temp_stop: { alternate_method: "", resume_date: "", ...(e.temp_stop || {}) },
  stop: { reason: "", move_out_date: "", ...(e.stop || {}) },
  notes: e.notes || "",
});

/**
 * Create / edit one PAD Info entry. Two steps: pick a category, then fill the fields that
 * category needs. No money and no banking details are captured here: the rent is read back
 * from the unit's mastersheet row, and account information never enters this system.
 */
const PadInfoModal = ({ isOpen, toggle, itemId, item, unitNo, buildingName, propertyName, tenantNames, incoming, existing, voidCheque, onSaved }) => {
  const [form, setForm] = useState(() => (existing ? fromExisting(existing) : emptyForm(tenantNames)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Attachments are staged, not uploaded on pick: a brand-new entry has nothing to hang a file
  // on yet, and Cancel must not leave an orphaned object in the bucket. Everything is applied
  // in one go by save().
  const [stagedFiles, setStagedFiles] = useState([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState([]);
  const [savingStep, setSavingStep] = useState("");
  // If the entry saves but an upload then fails, the entry still exists. Remembering its id
  // means pressing Save again edits that entry rather than creating a second one.
  const [createdId, setCreatedId] = useState(null);
  // Lets the "Attach file" button in the section header drive the panel's hidden input.
  const attachRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setForm(existing ? fromExisting(existing) : emptyForm(tenantNames));
      setError("");
      setStagedFiles([]);
      setRemovedAttachmentIds([]);
      setSavingStep("");
      setCreatedId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, existing?._id]);

  const meta = form.category ? categoryMeta(form.category) : null;
  const fields = meta ? meta.fields : [];
  // Rent is never entered or stored here — it is always the mastersheet's number.
  const totalRent = useMemo(() => totalRentFromItem(item), [item]);
  const rentBreakdown = useMemo(() => rentBreakdownFromItem(item), [item]);
  // A PAD Stop pre-fills its move-out date from the lease end column, but stays editable.
  const systemMoveOut = useMemo(() => moveOutDateFromItem(item), [item]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setSub = (key, patch) => setForm((f) => ({ ...f, [key]: { ...f[key], ...patch } }));

  const pickCategory = (value) => {
    const patch = { category: value };
    // A New PAD is usually for the incoming resident — the row may still show the departing one.
    if (value === "NEW" && !existing && incoming?.tenants && (!form.tenant_names || form.tenant_names === tenantNames)) patch.tenant_names = incoming.tenants;
    if (value !== "NEW" && !existing && form.tenant_names === incoming?.tenants) patch.tenant_names = tenantNames || "";
    // A New PAD starts when the lease starts — take the signed lease's start date.
    if (value === "NEW" && !existing && incoming?.start_date && !form.effective_date) patch.effective_date = incoming.start_date;
    // A PAD Stop is usually the end of the lease — offer that date, let them change it.
    if (value === "STOP" && systemMoveOut && !form.stop.move_out_date) patch.stop = { ...form.stop, move_out_date: systemMoveOut };
    set(patch);
  };

  const validate = () => {
    if (!form.category) return "Pick what kind of PAD update this is.";
    if (!form.effective_date) return "The effective date is required.";
    if (form.category === "DELAY" && form.delay.resume_date && form.delay.resume_date < form.effective_date) return "The PAD cannot resume before the pause starts.";
    if (form.category === "TEMP_STOP" && form.temp_stop.resume_date && form.temp_stop.resume_date < form.effective_date) return "The PAD cannot resume before the pause starts.";
    if (form.category === "TEMP_STOP" && !form.temp_stop.alternate_method) return "How is the resident paying meanwhile?";
    if (form.category === "STOP" && !form.stop.reason) return "Please pick a reason for stopping the PAD.";
    return "";
  };

  const save = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setSaving(true);
    setError("");
    const payload = {
      mastersheet_item_id: itemId,
      category: form.category,
      effective_date: form.effective_date,
      // The bank always pulls on the 1st; a Delay moves that month's pull to the chosen day.
      pull_day: form.category === "DELAY" ? Number(form.effective_date.slice(8, 10)) || 1 : 1,
      notes: form.notes,
      tenant_names: (form.tenant_names || tenantNames || "").trim(),
    };
    if (form.category === "DELAY") payload.delay = { month: monthOf(form.effective_date), resume_date: form.delay.resume_date };
    if (form.category === "TEMP_STOP") payload.temp_stop = form.temp_stop;
    if (form.category === "STOP") payload.stop = form.stop;
    try {
      // 1. The entry itself — a new one has to exist before anything can hang off it.
      setSavingStep(stagedFiles.length || removedAttachmentIds.length ? "Saving entry…" : "");
      let entryId = existing?._id || createdId;
      if (entryId) {
        await updatePadEntry(entryId, payload);
      } else {
        const res = await createPadEntry(payload);
        entryId = res?.data?.data?._id;
        if (!entryId) {
          if (stagedFiles.length) throw new Error("The entry saved but did not return an id, so the files were not attached.");
        } else {
          setCreatedId(entryId);
        }
      }

      // 2. Removals before uploads, so swapping a file for a better one cannot trip the
      //    ten-attachment ceiling.
      for (const attachmentId of [...removedAttachmentIds]) {
        setSavingStep("Removing attachments…");
        await removePadAttachment(entryId, attachmentId);
        // Drop it as it lands: a retry after a later failure must not repeat finished work.
        setRemovedAttachmentIds((prev) => prev.filter((x) => x !== attachmentId));
      }

      // 3. Upload each staged file, then record it against the entry.
      const toUpload = [...stagedFiles];
      for (let i = 0; i < toUpload.length; i++) {
        const file = toUpload[i];
        setSavingStep(`Uploading ${i + 1} of ${toUpload.length}…`);
        const uploaded = await uploadPadAttachmentFile(file);
        await addPadAttachment(entryId, {
          key: uploaded.key,
          filename: file.name,
          size: file.size,
          mime: file.type,
        });
        setStagedFiles((prev) => prev.filter((f) => f !== file));
      }

      const n = toUpload.length;
      toast.success(
        `${existing?._id ? "PAD info updated" : "PAD info recorded"}${n ? ` · ${n} file${n === 1 ? "" : "s"} attached` : ""}`
      );
      onSaved && onSaved();
      toggle();
    } catch (e) {
      const base = e?.response?.data?.message || e.message || "Failed to save PAD info";
      // Say plainly that the entry is already saved, or someone will re-type the whole thing.
      const msg = createdId || existing?._id ? `${base} The entry itself is saved — press Save again to finish the attachments.` : base;
      setError(msg);
      toast.error(base);
    } finally {
      setSaving(false);
      setSavingStep("");
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered contentClassName="pad-info-modal-content">
      <style>{`
        .pad-info-modal-content { border: none; border-radius: 18px; overflow: hidden; box-shadow: 0 24px 70px rgba(12,68,124,0.20); }
        .pad-info-modal-content input::placeholder, .pad-info-modal-content textarea::placeholder { color: #9AA7B8; }
        .pad-info-modal-content select:focus, .pad-info-modal-content input:focus, .pad-info-modal-content textarea:focus { outline: none; }
        .pim-focusable:focus-within { border-color: ${C.primaryMd}; box-shadow: 0 0 0 3px rgba(26,111,181,0.12); }

        /* Numbered step badge — the spine of the form. */
        .pim-step { width: 26px; height: 26px; border-radius: 999px; background: ${C.primary}; color: #fff;
          font-size: 12.5px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; flex: none; }

        /* Category cards: three across, roomy, with a check when chosen. */
        .pim-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
        @media (max-width: 720px) { .pim-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        @media (max-width: 480px) { .pim-grid { grid-template-columns: 1fr; } }
        .pim-cat { position: relative; border: 1.5px solid ${C.border}; border-radius: 12px; background: #fff;
          padding: 16px 14px; cursor: pointer; text-align: left; display: flex; gap: 12px; align-items: flex-start;
          transition: border-color .12s, box-shadow .12s, background .12s; font-family: inherit; width: 100%; }
        .pim-cat:hover { border-color: #B9C6D8; background: #FBFCFE; }
        .pim-cat.on { border-color: ${C.primaryMd}; background: #F6FAFF; box-shadow: 0 0 0 3px rgba(26,111,181,0.10); }
        .pim-cat .ic { width: 40px; height: 40px; border-radius: 11px; display: inline-flex; align-items: center;
          justify-content: center; flex: none; }
        .pim-cat .t { font-size: 14px; font-weight: 700; color: ${C.text}; line-height: 1.25; display: block; }
        .pim-cat .b { font-size: 12px; color: ${C.muted}; margin-top: 5px; line-height: 1.4; display: block; }
        .pim-cat .tick { position: absolute; top: 10px; right: 10px; width: 20px; height: 20px; border-radius: 999px;
          background: ${C.primaryMd}; color: #fff; display: inline-flex; align-items: center; justify-content: center; }

        .pim-btn { border: none; cursor: pointer; font-size: 13.5px; font-weight: 600; border-radius: 9px;
          padding: 10px 18px; display: inline-flex; align-items: center; gap: 7px; font-family: inherit; }
        .pim-btn-primary { background: ${C.primary}; color: #fff; box-shadow: 0 6px 18px rgba(12,68,124,0.28); }
        .pim-btn-primary:hover:not(:disabled) { background: #083460; }
        .pim-btn-primary:disabled { background: #94A3B8; box-shadow: none; cursor: not-allowed; }
        .pim-btn-ghost { background: #fff; color: ${C.text}; border: 1px solid ${C.inputBorder}; }
        .pim-btn-ghost:hover { background: #F1F5FA; }

        /* Outlined action that sits in a section header. */
        .pim-headbtn { border: 1px solid ${C.primaryMd}; background: #fff; color: ${C.primaryMd}; border-radius: 9px;
          padding: 8px 14px; font-size: 13px; font-weight: 600; display: inline-flex; align-items: center; gap: 7px;
          cursor: pointer; font-family: inherit; }
        .pim-headbtn:hover:not(:disabled) { background: #F1F7FE; }
        .pim-headbtn:disabled { opacity: .5; cursor: not-allowed; }

        .pim-iconbtn { background: none; border: none; cursor: pointer; color: ${C.muted}; display: inline-flex; }
        .pim-iconbtn:hover { color: ${C.text}; }
        .pim-select { border: none; outline: none; background: transparent; flex: 1; width: 100%; font-size: ${T.body}px; color: ${C.text}; height: 100%; font-family: inherit; }
      `}</style>

      {/* ---- header ---- */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 22px", borderBottom: `1px solid ${C.border}`, background: "#fff" }}>
        <span style={{ width: 40, height: 40, borderRadius: 11, background: "#EBF3FC", color: C.primary, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
          <FeatherIcon icon="credit-card" size={19} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: T.title, fontWeight: 700, color: C.text, lineHeight: 1.2 }}>
            {existing?._id ? "Edit PAD info" : "Record PAD info"}
          </div>
          <div style={{ fontSize: T.hint, color: C.muted, marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {[unitNo ? `Unit ${unitNo}` : "", buildingName, propertyName, tenantNames].filter(Boolean).join(" · ")}
          </div>
        </div>
        <button type="button" className="pim-iconbtn" onClick={toggle} aria-label="Close">
          <FeatherIcon icon="x" size={19} />
        </button>
      </div>

      <div style={styles.body}>
        {/* ---- 1. category ---- */}
        <Section n={1} title="What kind of PAD update is this?" subtitle="Choose the type of PAD update you want to record.">
          <div className="pim-grid">
            {PAD_CATEGORIES.map((c) => (
              <button key={c.value} type="button" className={`pim-cat${form.category === c.value ? " on" : ""}`} onClick={() => pickCategory(c.value)}>
                <span className="ic" style={{ background: c.bg, color: c.color }}>
                  <FeatherIcon icon={c.icon} size={19} />
                </span>
                <span style={{ minWidth: 0 }}>
                  <span className="t">{c.label}</span>
                  <span className="b">{c.blurb}</span>
                </span>
                {form.category === c.value ? (
                  <span className="tick"><FeatherIcon icon="check" size={12} /></span>
                ) : null}
              </button>
            ))}
          </div>
        </Section>

        {/* ---- 2. details ---- */}
        {meta && (
          <Section n={2} title={`${meta.label} details`} subtitle={`Enter the details for this ${meta.label.toLowerCase()}.`}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
              {form.category === "NEW" && (
                <Field
                  label="Resident(s) this PAD is for"
                  style={{ gridColumn: "1 / -1" }}
                  hint={incoming?.tenants && form.tenant_names === incoming.tenants ? `Pre-filled from the signed lease starting ${incoming.start_date || "soon"}.` : undefined}
                >
                  <Box>
                    <FeatherIcon icon="user" size={15} color={C.muted} />
                    <input style={styles.bareInput} placeholder="Name(s) on the lease" value={form.tenant_names} onChange={(e) => set({ tenant_names: e.target.value })} />
                  </Box>
                </Field>
              )}

              <Field
                label={meta.dateLabel || "Effective from"}
                hint={
                  form.category === "NEW" && incoming?.start_date && form.effective_date === incoming.start_date
                    ? "Taken from the signed lease's start date."
                    : undefined
                }
              >
                <Box>
                  <FeatherIcon icon="calendar" size={15} color={C.muted} />
                  <input type="date" style={styles.bareInput} value={form.effective_date} onChange={(e) => set({ effective_date: e.target.value })} />
                </Box>
              </Field>

              {/* Total rent — read-only, always the mastersheet's number. */}
              <Field
                label="Total rent"
                hint={
                  rentBreakdown.length
                    ? `From the mastersheet: ${rentBreakdown.map((r) => `${r.label} ${money(r.amount)}`).join(" + ")}. Change it through an Addendum or the Lease flow.`
                    : "No rent columns are filled on this unit's mastersheet row yet."
                }
              >
                <Box style={{ background: C.soft }}>
                  <FeatherIcon icon="dollar-sign" size={15} color={C.muted} />
                  <span style={{ ...styles.bareInput, display: "flex", alignItems: "center", fontWeight: 700 }}>
                    {totalRent === null ? "—" : money(totalRent)}
                  </span>
                  <FeatherIcon icon="lock" size={13} color={C.muted} />
                </Box>
              </Field>

              {fields.includes("delay_resume") && (
                <Field label="PAD resumes on" hint="Leave blank if open-ended.">
                  <Box>
                    <FeatherIcon icon="play-circle" size={15} color={C.muted} />
                    <input type="date" style={styles.bareInput} value={form.delay.resume_date} onChange={(e) => setSub("delay", { resume_date: e.target.value })} />
                  </Box>
                </Field>
              )}

              {fields.includes("temp_resume") && (
                <Field label="PAD resumes on" hint="Leave blank if open-ended.">
                  <Box>
                    <FeatherIcon icon="play-circle" size={15} color={C.muted} />
                    <input type="date" style={styles.bareInput} value={form.temp_stop.resume_date} onChange={(e) => setSub("temp_stop", { resume_date: e.target.value })} />
                  </Box>
                </Field>
              )}

              {fields.includes("temp_method") && (
                <Field label="Paying meanwhile by">
                  <Box>
                    <FeatherIcon icon="repeat" size={15} color={C.muted} />
                    <select className="pim-select" value={form.temp_stop.alternate_method} onChange={(e) => setSub("temp_stop", { alternate_method: e.target.value })}>
                      <option value="">Select…</option>
                      {ALTERNATE_METHODS.map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </Box>
                </Field>
              )}

              {fields.includes("stop_reason") && (
                <Field label="Reason">
                  <Box>
                    <FeatherIcon icon="help-circle" size={15} color={C.muted} />
                    <select className="pim-select" value={form.stop.reason} onChange={(e) => setSub("stop", { reason: e.target.value })}>
                      <option value="">Select…</option>
                      {STOP_REASONS.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </Box>
                </Field>
              )}

              {fields.includes("stop_move_out") && (
                <Field
                  label="Move-out date"
                  hint={systemMoveOut && form.stop.move_out_date === systemMoveOut ? "Picked up from the lease end date — edit if it differs." : undefined}
                >
                  <Box>
                    <FeatherIcon icon="log-out" size={15} color={C.muted} />
                    <input type="date" style={styles.bareInput} value={form.stop.move_out_date} onChange={(e) => setSub("stop", { move_out_date: e.target.value })} />
                  </Box>
                </Field>
              )}
            </div>
          </Section>
        )}

        {/* ---- 3. attachments ---- */}
        {meta && (
          <Section
            n={3}
            title="Attachments"
            subtitle="Upload any supporting documents."
            action={
              <button type="button" className="pim-headbtn" disabled={saving} onClick={() => attachRef.current && attachRef.current()}>
                <FeatherIcon icon="paperclip" size={14} /> Attach file
              </button>
            }
          >
            <PadAttachments
              entryId={existing?._id || createdId}
              saved={existing?.attachments || []}
              staged={stagedFiles}
              removedIds={removedAttachmentIds}
              onStage={(files) => setStagedFiles((prev) => [...prev, ...files])}
              onUnstage={(i) => setStagedFiles((prev) => prev.filter((_, idx) => idx !== i))}
              onToggleRemove={(id) =>
                setRemovedAttachmentIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
              }
              voidCheque={voidCheque}
              canWrite
              busy={saving}
              openPickerRef={attachRef}
            />
          </Section>
        )}

        {/* ---- 4. notes ---- */}
        {meta && (
          <Section n={4} title="Notes for accounting" subtitle="Add any notes or instructions for your accounting team." last>
            <div className="pim-focusable" style={{ border: `1px solid ${C.inputBorder}`, borderRadius: 10, background: "#fff", padding: "10px 12px", display: "flex", gap: 8 }}>
              <FeatherIcon icon="file-text" size={15} color={C.muted} style={{ flex: "none", marginTop: 2 }} />
              <textarea
                rows={3}
                style={{ ...styles.bareInput, height: "auto", resize: "vertical", width: "100%" }}
                placeholder="Anything accounting should know — e.g. “Sending e-transfer for Jan”, “SD received”, “Joel's suite”…"
                value={form.notes}
                onChange={(e) => set({ notes: e.target.value })}
              />
            </div>
          </Section>
        )}
      </div>

      {/* ---- footer ---- */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "14px 22px", borderTop: `1px solid ${C.border}`, background: "#fff" }}>
        <span
          style={{
            display: "flex", alignItems: "center", gap: 8, minWidth: 0,
            border: `1px solid ${error ? "#F3D2CF" : C.border}`, background: error ? "#FEF6F5" : C.soft,
            borderRadius: 9, padding: "8px 12px",
          }}
        >
          <FeatherIcon icon={error ? "alert-circle" : "info"} size={14} color={error ? "#A32D2D" : C.muted} style={{ flex: "none" }} />
          <span style={{ ...styles.hint, color: error ? "#A32D2D" : C.muted, fontWeight: error ? 600 : 500 }}>
            {error || (meta ? meta.blurb : "Start by picking a category above.")}
          </span>
        </span>
        <div style={{ display: "flex", gap: 8, flex: "none" }}>
          <button
            type="button"
            className="pim-btn pim-btn-ghost"
            onClick={() => {
              // A failed save may have already created the entry — refresh so the list is honest.
              if (createdId) onSaved && onSaved();
              toggle();
            }}
            disabled={saving}
          >
            {createdId ? "Close" : "Cancel"}
          </button>
          <button type="button" className="pim-btn pim-btn-primary" onClick={save} disabled={saving || !meta}>
            {saving ? <Spinner size="sm" /> : <FeatherIcon icon="save" size={14} />}
            {saving && savingStep ? savingStep : existing?._id ? "Save changes" : "Record PAD info"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PadInfoModal;
